import { ShippingMethod } from "components/modals/CommissionDetailsModal";

export interface CommissionCalculationInput {
  price: number;
  specialPrice?: number | null;
  discountStartDate?: string | null;
  discountEndDate?: string | null;
  weight: number;
  length: number; // longueur25
  width: number;  // dimensions_de_l_outil_largeur1
  height: number; // dimensions_de_l_outil_hauteur1
  shippingMethod: number;
  percentCommission?: number; // Default percentage if not provided
}

export interface CommissionCalculationResult {
  fixCommission: number;
  percentCommission: number;
  percentCommissionValue: number;
  applicablePrice: number;
  regularPrice: number;
  specialPrice: number | null;
  totalCommission: number;
  isSpecialPriceApplicable: boolean;
  volumetricWeight: number;
  taxableWeight: number;
  calculationDetails: {
    shippingMethodName: string;
    baseAmount: number;
    coefficient: number;
    formula: string;
  };
}

export const calculateCommission = (input: CommissionCalculationInput): CommissionCalculationResult => {
  // Determine if special price applies first to get the correct applicable price
  const currentDate = new Date();
  const specialPriceStartDate = input.discountStartDate ? new Date(input.discountStartDate) : null;
  const specialPriceEndDate = input.discountEndDate ? new Date(input.discountEndDate) : null;
  
  const hasValidSpecialPrice = 
    input.specialPrice !== undefined && 
    input.specialPrice !== null && 
    Number(input.specialPrice) > 0;
  
  const isSpecialPriceApplicable = Boolean(
    specialPriceStartDate && 
    specialPriceEndDate && 
    currentDate >= specialPriceStartDate && 
    currentDate <= specialPriceEndDate && 
    hasValidSpecialPrice
  );
  
  // Use special price if applicable, otherwise use regular price
  const applicablePrice = isSpecialPriceApplicable 
    ? Number(input.specialPrice || 0)
    : Number(input.price || 0);

  // Calculate commission percentage based on price tiers
  let percentCommission = 15; // default fallback
  
  if (applicablePrice <= 100) {
    percentCommission = 12;
  } else if (applicablePrice <= 200) {
    percentCommission = 10;
  } else if (applicablePrice <= 300) {
    percentCommission = 8;
  } else if (applicablePrice <= 500) {
    percentCommission = 7;
  } else if (applicablePrice <= 1000) {
    percentCommission = 5;
  } else {
    percentCommission = 3;
  }
  
  // Allow override if provided
  const finalPercentCommission = input.percentCommission || percentCommission;
  
  // Use default values for missing dimensions (assume small package if not provided)
  const length = input.length || 10; // 10cm default
  const width = input.width || 10;   // 10cm default
  const height = input.height || 5;  // 5cm default
  const weight = input.weight || 0.1; // 100g default
  
  // Calculate volumetric weight
  const volumetricWeight = (length * width * height) / 5000;
  
  // Use the greater of actual weight or volumetric weight
  const taxableWeight = Math.max(volumetricWeight, weight);
  
  // Determine fix commission based on shipping method
  let baseAmount = 0;
  let coefficient = 0;
  let shippingMethodName = "";
  
  switch (Number(input.shippingMethod)) {
    case ShippingMethod.FBW:
      shippingMethodName = "Fulfilled By Wamia";
      baseAmount = 1;
      coefficient = 0.6;
      break;
    case ShippingMethod.ESS:
      shippingMethodName = "Easy Shipping By Seller";
      baseAmount = 1;
      coefficient = 0.5;
      break;
    case ShippingMethod.ESW:
      shippingMethodName = "Easy Shipping By Wamia";
      baseAmount = 1.5;
      coefficient = 0.6;
      break;
  }
  
  const fixCommission = baseAmount + (taxableWeight * coefficient);
  
  // Calculate percentage commission
  const percentCommissionValue = applicablePrice * (finalPercentCommission / 100);
  const totalCommission = fixCommission + percentCommissionValue;
  
  const formula = `${baseAmount} + (${taxableWeight.toFixed(3)} × ${coefficient})`;
  
  return {
    fixCommission: parseFloat(fixCommission.toFixed(2)),
    percentCommission: finalPercentCommission,
    percentCommissionValue: parseFloat(percentCommissionValue.toFixed(2)),
    applicablePrice,
    regularPrice: Number(input.price || 0),
    specialPrice: input.specialPrice ? Number(input.specialPrice) : null,
    totalCommission: parseFloat(totalCommission.toFixed(2)),
    isSpecialPriceApplicable,
    volumetricWeight: parseFloat(volumetricWeight.toFixed(3)),
    taxableWeight: parseFloat(taxableWeight.toFixed(3)),
    calculationDetails: {
      shippingMethodName,
      baseAmount,
      coefficient,
      formula
    }
  };
};

// Helper function to validate if all required fields are available for calculation
export const canCalculateCommission = (formValues: any): boolean => {
  // Check if we have a valid price (regular or special price if in date range)
  const hasRegularPrice = formValues.price && Number(formValues.price) > 0;
  
  // Check if special price is applicable and valid
  const hasSpecialPrice = formValues.special_price && Number(formValues.special_price) > 0;
  const hasSpecialDates = formValues.special_from_date && formValues.special_to_date;
  
  let hasValidPrice = hasRegularPrice;
  
  // If special price exists with dates, check if it's currently active
  if (hasSpecialPrice && hasSpecialDates) {
    const currentDate = new Date();
    const startDate = new Date(formValues.special_from_date);
    const endDate = new Date(formValues.special_to_date);
    
    if (currentDate >= startDate && currentDate <= endDate) {
      hasValidPrice = true; // Special price is active
    }
  }
  
  // Check if at least one dimension exists
  const dimensionFields = ['longueur25', 'dimensions_de_l_outil_largeur1', 'dimensions_de_l_outil_hauteur1'];
  const hasAtLeastOneDimension = dimensionFields.some(field => {
    const value = formValues[field];
    return value && Number(value) > 0;
  });
  
  // Check if shipping method is set
  const hasShippingMethod = (() => {
    if (!formValues.mode_expedition) return false;
    
    // If it's a number or a string that can be converted to a number
    if (typeof formValues.mode_expedition === 'number' || 
        (typeof formValues.mode_expedition === 'string' && !isNaN(Number(formValues.mode_expedition)) && Number(formValues.mode_expedition) > 0)) {
      return true;
    }
    
    // If it's an object with option_id
    if (typeof formValues.mode_expedition === 'object') {
      if (Array.isArray(formValues.mode_expedition)) {
        // If it's an array with at least one object that has option_id
        return formValues.mode_expedition.length > 0 && 
               typeof formValues.mode_expedition[0] === 'object' && 
               'option_id' in formValues.mode_expedition[0] &&
               Number(formValues.mode_expedition[0].option_id) > 0;
      } else {
        // If it's a single object with option_id
        return formValues.mode_expedition !== null && 
               'option_id' in formValues.mode_expedition &&
               Number(formValues.mode_expedition.option_id) > 0;
      }
    }
    
    return false;
  })();
  
  // Enable button if we have a valid price, at least one dimension, and a shipping method
  const canCalculate = hasValidPrice && hasAtLeastOneDimension && hasShippingMethod;
  
  console.log('Commission calculation validation:', {
    hasRegularPrice,
    hasSpecialPrice,
    hasSpecialDates,
    hasValidPrice,
    hasAtLeastOneDimension,
    canCalculate,
    formValues: {
      price: formValues.price,
      special_price: formValues.special_price,
      special_from_date: formValues.special_from_date,
      special_to_date: formValues.special_to_date,
      longueur25: formValues.longueur25,
      dimensions_de_l_outil_largeur1: formValues.dimensions_de_l_outil_largeur1,
      dimensions_de_l_outil_hauteur1: formValues.dimensions_de_l_outil_hauteur1
    }
  });
  
  return canCalculate;
};
